<template>
    <portal to="modals">
        <modal 
            :name="name" 
            :adaptive="true"
            :scrollable="true"
            :click-to-close="allowClose"
            height="auto"
            @before-close="beforeClose"
            @before-open="beforeOpen"
        >
            <div class="p-xl">
                <div 
                    v-if="title" 
                    class="flex items-center mb-3xl"
                >
                    <h1 class="m-0 text-md">{{ title }}</h1>
                </div>

                <div v-if="saveErrors.length">
                    <div v-for="error in saveErrors" class="alert is-error font-regular">
                        <div class="w-full flex flex-col">
                            <div class="flex items-center">
                                <div class="alert-icon">
                                    <font-awesome-icon icon="exclamation" />
                                </div>

                                <span>{{ error.message }}</span>
                            </div>

                            <ul
                                v-if="error.validationErrors"
                                class="list-reset mt-lg block"
                            >
                                <li v-for="validationError in error.validationErrors">
                                    {{ validationError[0] }}
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                <form
                    ref="form"
                    v-loading="loading"
                    autocomplete="off"
                    @submit.prevent="save"
                >
                    <slot />

                    <div class="button-group mt-3xl">
                        <button
                            v-if="allowClose"
                            type="button" 
                            class="btn is-ghost btn-lg" 
                            @click="hide"
                        >
                            {{ closeButtonText }}
                        </button>
                        <slot name="primary-action">
                            <button 
                                type="submit"
                                class="btn btn-lg"
                                :class="primaryButtonClass"
                            >
                                {{ primaryButtonText }}
                            </button>
                        </slot>
                    </div>
                </form>
            </div>
        </modal>
    </portal>
</template>

<script>
    export default {
        props: {
            title: {
                type: String,
                default: '',
            },
            name: {
                required: true,
                type: String,
            },
            primaryButtonText: {
                type: String,
                default: lang('Spara', 'Save'),
            },
            primaryButtonClass: {
                type: String,
                default: 'is-primary',
            },
            closeButtonText: {
                type: String,
                default: lang('Avbryt', 'Cancel'),
            },
            method: {
                type: String,
                default: 'post',
            },
            action: {
                type: String,
                required: true,
            },
            files: {
                type: Boolean,
                default: false,
            },
            redirectTo: {
                type: String,
                default: null,
            },
            callback: {
                type: Function,
                default: null,
            },
            openOnPageLoad: {
                type: Boolean,
                default: false,
            },
            allowClose: {
                type: Boolean,
                required: false,
                default: true,
            },
            hideWhenDone: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                loading: false,
                saveErrors: [],
            };
        },
        mounted() {
            if (this.openOnPageLoad) {
                this.$nextTick(() => {
                    this.$modal.show(this.name);
                });
            }
        },
        methods: {
            hide() {
                this.$modal.hide(this.name);
            },
            save() {
                this.saveErrors = [];
                this.loading = true;

                const formData = new FormData(this.$refs.form);
                const config = {};
                let method = this.method;

                if (method.toLowerCase() === 'patch') {
                    method = 'post';
                    formData.append('_method', 'PATCH');
                }

                if (this.files === true) {
                    config.headers = {
                        'Content-Type': 'multipart/form-data',
                    };
                }

                axios[method](this.action, formData, config)
                    .then((response) => {
                        if (response.data.hasOwnProperty('redirectTo')) {
                            window.location = response.data.redirectTo;

                            return;
                        }

                        if (this.redirectTo !== null) {
                            window.location = this.redirectTo;

                            return;
                        }

                        this.loading = false;

                        if (this.callback) {
                            this.callback(response);
                        }

                        if (this.hideWhenDone) {
                            this.hide();
                        }
                    })
                    .catch((error) => {
                        if (error.response.status === 422 && error.response.data.errors) {
                            this.saveErrors.push({
                                message: lang('Någonting gick fel:','Something went wrong'),
                                validationErrors: error.response.data.errors,
                            });
                        } else if (error.response.status === 403) {
                            this.saveErrors.push({
                                message: lang(
                                    'Du har inte behörighet att utföra åtgärden',
                                    'You don\'t have permission to perform this action'
                                ),
                            });
                        } else {
                            const defaultErrorMessage = lang(
                                'Någonting gick fel när åtgärden försökte genomföras',
                                'Something went wrong when trying to execute action'
                            );

                            const responseErrorMessage = error.response.data.message;

                            this.saveErrors.push({
                                message: responseErrorMessage || defaultErrorMessage,
                            });
                        }

                        this.loading = false;
                    });
            },
            beforeOpen(event) {
                this.$emit('before-open', event);
            },
            beforeClose(event) {
                this.$emit('before-close', event);
                this.$emit('hide');
            },
        },
    };
</script>